import { useState } from 'react';
import { AlertTriangle, FileDown, FileText, Loader2, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  baixarRelatorioApontamentosIbsCbs,
  type FiltroApontamentosIbsCbs,
  type FormatoDoArquivo,
  type LinhaApontamentoIbsCbsApi,
  useRelatorioApontamentosIbsCbs,
} from '@/services/RelatorioApontamentosIbsCbs';
import { erroDoFiltroApontamentos } from './relatorio-apontamentos';

export const TITULO_DO_RELATORIO = 'Entradas — apontamentos das regras de IBS e CBS';

function competenciaAtual(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
}

function dataBr(iso: string): string {
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : '';
}

function valor(numero: number | null): string {
  return numero === null
    ? ''
    : numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function baixar(arquivo: Blob, nome: string) {
  const url = URL.createObjectURL(arquivo);
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

interface RelatorioApontamentosIbsCbsProps {
  /** A loja escolhida no cabeçalho da Central (CNPJ do contribuinte), como nos outros relatórios. */
  cnpjContribuinte: string | null;
}

/**
 * O relatório da SPEC-008, na tela da Central do TurboPro, no grupo de entradas, ao lado do catálogo comum.
 * Não é um relatório do catálogo: vem do módulo da Reforma e não tem agendamento, campos configuráveis nem
 * PDF em lote (este último depende da SPEC-015).
 */
export function RelatorioApontamentosIbsCbs({
  cnpjContribuinte,
}: RelatorioApontamentosIbsCbsProps) {
  const { toast } = useToast();
  const [competenciaInicial, setCompetenciaInicial] = useState(competenciaAtual);
  const [competenciaFinal, setCompetenciaFinal] = useState(competenciaAtual);
  const [filtroConsultado, setFiltroConsultado] = useState<FiltroApontamentosIbsCbs | null>(null);
  const [baixando, setBaixando] = useState<FormatoDoArquivo | null>(null);

  const filtro: FiltroApontamentosIbsCbs = {
    competenciaInicial,
    competenciaFinal,
    cnpjContribuinte: cnpjContribuinte ?? '',
  };
  const erro = erroDoFiltroApontamentos(filtro);

  const consulta = useRelatorioApontamentosIbsCbs(filtroConsultado);
  const relatorio = consulta.data?.status?.resultado ? consulta.data.data : null;
  const recusa =
    consulta.data && !consulta.data.status?.resultado ? consulta.data.status.mensagem : null;

  const handleBaixar = async (formato: FormatoDoArquivo) => {
    if (erro) return;
    setBaixando(formato);
    try {
      const { arquivo, nome } = await baixarRelatorioApontamentosIbsCbs(filtro, formato);
      baixar(arquivo, nome);
    } catch (falha) {
      toast({
        variant: 'destructive',
        title: 'Erro na exportação',
        description: falha instanceof Error ? falha.message : 'Não foi possível gerar o arquivo.',
      });
    } finally {
      setBaixando(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-base font-semibold">{TITULO_DO_RELATORIO}</CardTitle>
          <Badge variant="outline">Entrada</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          As notas de entrada do período com apontamento da conferência das regras de IBS e CBS, ou
          com crédito a apropriar. Nota sem apontamento e sem crédito a apropriar não aparece.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2 flex-wrap">
          <label className="text-sm space-y-1">
            <span className="block text-muted-foreground">Competência inicial</span>
            <Input
              type="month"
              aria-label="Competência inicial"
              value={competenciaInicial}
              onChange={e => setCompetenciaInicial(e.target.value)}
              className="h-9 w-44"
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="block text-muted-foreground">Competência final</span>
            <Input
              type="month"
              aria-label="Competência final"
              value={competenciaFinal}
              onChange={e => setCompetenciaFinal(e.target.value)}
              className="h-9 w-44"
            />
          </label>
          <Button
            size="sm"
            className="h-9 gap-1.5"
            disabled={!!erro}
            onClick={() => setFiltroConsultado({ ...filtro })}
          >
            <Search className="h-3.5 w-3.5" />
            Consultar
          </Button>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-1.5"
            disabled={!!erro || baixando !== null}
            onClick={() => handleBaixar('Pdf')}
          >
            {baixando === 'Pdf' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileText className="h-3.5 w-3.5" />
            )}
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-1.5"
            disabled={!!erro || baixando !== null}
            onClick={() => handleBaixar('Csv')}
          >
            {baixando === 'Csv' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileDown className="h-3.5 w-3.5" />
            )}
            CSV
          </Button>
        </div>

        {erro && <p className="text-sm text-muted-foreground">{erro}</p>}
        {recusa && (
          <p role="alert" className="text-sm text-destructive">
            {recusa}
          </p>
        )}
        {consulta.isFetching && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Consultando...
          </p>
        )}

        {relatorio && (
          <>
            <section aria-label="Classificações da tabela com CST divergente" className="space-y-2">
              <h4 className="text-sm font-semibold">Classificações da tabela com CST divergente</h4>
              {relatorio.ClassificacoesComCstDivergente.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhuma: o CST de toda linha da tabela de regras é o da classificação.
                </p>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Classificação</TableHead>
                        <TableHead>CST da linha</TableHead>
                        <TableHead>CST esperado</TableHead>
                        <TableHead className="text-right">Notas do período</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {relatorio.ClassificacoesComCstDivergente.map(divergente => (
                        <TableRow key={`${divergente.Classificacao}-${divergente.CstDaLinha}`}>
                          <TableCell className="font-mono text-sm">
                            <span className="inline-flex items-center gap-1">
                              <AlertTriangle
                                className="h-3.5 w-3.5 text-amber-600"
                                aria-hidden="true"
                              />
                              {divergente.Classificacao}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {divergente.CstDaLinha}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {divergente.CstEsperado}
                          </TableCell>
                          <TableCell className="text-right text-sm">{divergente.Notas}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </section>

            <section aria-label="Notas com apontamento" className="space-y-2">
              <h4 className="text-sm font-semibold">
                Notas com apontamento ou crédito a apropriar ({relatorio.Linhas.length})
              </h4>
              {relatorio.Linhas.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhuma nota do período pede atenção.
                </p>
              ) : (
                <TabelaDeLinhas linhas={relatorio.Linhas} />
              )}
            </section>
          </>
        )}
      </CardContent>
    </Card>
  );
}

const COLUNAS = [
  'Número',
  'Série',
  'Emissão',
  'Emitente',
  'Loja',
  'Item',
  'CFOP',
  'CST',
  'Classificação',
  'Apontamento',
  'Severidade',
  'Campo',
  'Esperado',
  'Encontrado',
  'Regra',
  'IBS destacado',
  'CBS destacado',
  'IBS a apropriar',
  'CBS a apropriar',
  'Motivo',
];

function TabelaDeLinhas({ linhas }: { linhas: LinhaApontamentoIbsCbsApi[] }) {
  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            {COLUNAS.map(coluna => (
              <TableHead key={coluna} className="whitespace-nowrap">
                {coluna}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {linhas.map((linha, indice) => (
            <TableRow key={indice}>
              <TableCell className="text-sm">{linha.Numero}</TableCell>
              <TableCell className="text-sm">{linha.Serie}</TableCell>
              <TableCell className="text-sm whitespace-nowrap">
                {dataBr(linha.DataEmissao)}
              </TableCell>
              <TableCell className="text-sm" title={linha.EmitenteCnpj ?? undefined}>
                {linha.EmitenteNome || linha.EmitenteCnpj}
              </TableCell>
              <TableCell className="font-mono text-sm">{linha.Loja}</TableCell>
              <TableCell className="text-sm">{linha.NumItem ?? 'nota'}</TableCell>
              <TableCell className="font-mono text-sm">{linha.Cfop}</TableCell>
              <TableCell className="font-mono text-sm">{linha.Cst}</TableCell>
              <TableCell className="font-mono text-sm">{linha.ClassTrib}</TableCell>
              <TableCell className="font-mono text-sm" title={linha.Descricao ?? undefined}>
                {linha.Codigo}
              </TableCell>
              <TableCell className="text-sm">
                {linha.Severidade && (
                  <Badge variant={linha.Severidade === 'erro' ? 'destructive' : 'secondary'}>
                    {linha.Severidade}
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-sm">{linha.Campo}</TableCell>
              <TableCell className="text-sm">{linha.Esperado}</TableCell>
              <TableCell className="text-sm">{linha.Encontrado}</TableCell>
              <TableCell className="text-sm">{linha.Regra}</TableCell>
              <TableCell className="text-sm text-right">{valor(linha.DestacadoIbs)}</TableCell>
              <TableCell className="text-sm text-right">{valor(linha.DestacadoCbs)}</TableCell>
              <TableCell className="text-sm text-right">{valor(linha.AApropriarIbs)}</TableCell>
              <TableCell className="text-sm text-right">{valor(linha.AApropriarCbs)}</TableCell>
              <TableCell className="text-sm min-w-[220px]">{linha.Motivo}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
